/**
 * PendingChangesToolbar Component
 * 
 * Displays a toolbar when there are unsaved changes in the table.
 * Provides Save All and Discard All actions.
 */

import React, { useState } from 'react';
import { Save, X, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { VersionConflictDialog } from '@/components/dialogs/VersionConflictDialog';
import { VersionConflict } from '@/hooks/usePendingChanges';

interface PendingChangesToolbarProps {
  pendingCount: number;
  hasPendingChanges: boolean;
  onSaveAll: () => Promise<{ success: boolean; conflicts: VersionConflict[] }>;
  onDiscardAll: () => void;
  onForceSaveItem: (itemId: string) => Promise<boolean>;
  onDiscardItem: (itemId: string) => void;
}

export function PendingChangesToolbar({
  pendingCount,
  hasPendingChanges,
  onSaveAll,
  onDiscardAll,
  onForceSaveItem,
  onDiscardItem,
}: PendingChangesToolbarProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [conflicts, setConflicts] = useState<VersionConflict[]>([]);
  const [showConflictDialog, setShowConflictDialog] = useState(false);

  if (!hasPendingChanges) {
    return null;
  }

  const handleSaveAll = async () => {
    setIsSaving(true);
    try {
      const result = await onSaveAll();
      if (result.conflicts.length > 0) {
        setConflicts(result.conflicts);
        setShowConflictDialog(true);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleConflictResolve = async (itemId: string, action: 'save' | 'discard') => {
    if (action === 'save') {
      await onForceSaveItem(itemId);
    } else {
      onDiscardItem(itemId);
    }
    
    // Remove this conflict from the list
    setConflicts((prev) => prev.filter((c) => c.itemId !== itemId));
    
    // Close dialog if no more conflicts
    if (conflicts.length <= 1) {
      setShowConflictDialog(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-3 px-4 py-2 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800">
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <span className="text-sm text-amber-800 dark:text-amber-200">
          You have unsaved changes
        </span>
        <Badge 
          variant="secondary" 
          className="bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200"
        >
          {pendingCount} {pendingCount === 1 ? 'item' : 'items'}
        </Badge>
        
        <div className="flex-1" />
        
        <Button
          variant="outline"
          size="sm"
          onClick={onDiscardAll}
          disabled={isSaving}
          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
        >
          <X className="h-4 w-4 mr-1" />
          Discard All
        </Button>
        
        <Button
          size="sm"
          onClick={handleSaveAll}
          disabled={isSaving}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-1" />
          )}
          Save All Changes
        </Button>
      </div>

      <VersionConflictDialog
        open={showConflictDialog}
        onOpenChange={setShowConflictDialog}
        conflicts={conflicts}
        onResolve={handleConflictResolve}
      />
    </>
  );
}

export default PendingChangesToolbar;


















